import { type CSSProperties, type ComponentProps } from "react";

import { FormRow, Select, Tooltip } from "@skilitics/designer-ui-components";
import { useFormatMessage } from "@skilitics/localization";

import { useEditingCss } from "../properties/EditingCssPropertiesContext";
import { ElementStatePicker } from "./ElementStatePicker";
import { useLayoutAncestry } from "./LayoutAncestryContext";
import type { ElementStateOption } from "./types";
import { useContainingElementState, useSetActiveContainerElementState } from "./useActiveElementState";

type Props = {
  nodeId: number;
  allowedOptions: ElementStateOption[];
  disableTransition?: boolean;
};

export const ElementStateForm = ({ nodeId, allowedOptions, disableTransition }: Props) => {
  const formatMessage = useFormatMessage(formatStrings);
  const { cssProperties, onCssPropertiesChange } = useEditingCss();
  const setActiveContainerElementState = useSetActiveContainerElementState();
  const containing = useContainingElementState(useLayoutAncestry());

  const disabled = !!containing && containing?.nodeId !== nodeId;
  const handleTransitionChange: ComponentProps<typeof Select>["onChange"] = (e) => {
    const value = e.target.value as string;
    onCssPropertiesChange?.({ transition: convertSpeedToTransitionCss(value) });
  };

  return (
    <>
      <Tooltip title={disabled ? formatMessage("elementStateDisabledReason") : undefined} placement="left">
        <FormRow style={{ marginBottom: "10px" }}>
          <ElementStatePicker
            value={containing?.elementState ?? "default"}
            onChange={(elementState) => {
              setActiveContainerElementState(nodeId, elementState);
            }}
            disabled={disabled}
            allowedOptions={allowedOptions}
          />
        </FormRow>
      </Tooltip>
      <FormRow>
        <Select
          label={formatMessage("transitionLabel")}
          shrinkLabel
          value={disableTransition ? "" : convertTransitionCssToSpeed(cssProperties.transition)}
          onChange={handleTransitionChange}
          disabled={disableTransition}
          options={[
            { value: "", label: "Instant" },
            { value: "fast", label: "Fast" },
            { value: "medium", label: "Medium" },
            { value: "slow", label: "Slow" },
          ]}
        />
      </FormRow>
    </>
  );
};

const convertTransitionCssToSpeed = (transition: CSSProperties["transition"]) => {
  switch (transition) {
    case "all 0.2s ease-in-out":
      return "fast";
    case "all 0.5s ease-in-out":
      return "medium";
    case "all 0.8s ease-in-out":
      return "slow";
    default:
      return "";
  }
};

const convertSpeedToTransitionCss = (speed: string): CSSProperties["transition"] => {
  switch (speed) {
    case "fast":
      return "all 0.2s ease-in-out";
    case "medium":
      return "all 0.5s ease-in-out";
    case "slow":
      return "all 0.8s ease-in-out";
    default:
      return "";
  }
};

const formatStrings = {
  elementStateDisabledReason: {
    en: "Currently overwriting styles for another element, switch that element back to default to edit this element's styles.",
  },
  transitionLabel: {
    en: "Transition Speed",
  },
};
